
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FaFileDownload } from "react-icons/fa";
import { Table } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { CSVLink } from "react-csv"

import type { Comment } from './Stats.js';
import Filters from './Filters';
import Fuse from 'fuse.js';

type Props = {
    comments: Comment[];
    theme: string;
};

export type CSVComment = {
    comment: string;
    position: string;
    streck: string;
    count: number;
    date: string;
    time: string;

}

const keys = ['comment', 'position', 'streck'];
const SCORE_THRESHOLD = 0.4


const CommentsTable: FC<Props> = ({ comments, theme }) => {
    const [dateFilter, setDateFilter] = useState<string | null>(null);
    const [authorFilter, setAuthorFilter] = useState<string | null>(null);
    const [streckFilter, setStreckFilter] = useState<string | null>(null);
    const [dateStrComments, setDateStrComments] = useState<Comment[]>([]);
    const [filteredComments, setFilteredComments] = useState<Comment[]>(comments);
    const [csvComments, setCsvComments] = useState<Comment[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const commentsEndRef = useRef(null);

    const headers = [
        { label: 'Post', key: 'position' },
        { label: 'Kommentar', key: 'comment' },
        { label: 'Streck', key: 'streck' },
        { label: 'Antal', key: 'count' },
        { label: 'Datum', key: 'date' },
        { label: 'Tid', key: 'time'}
    ]

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleFilterChange = (filter: string, value: string) => {
        switch (filter) {
            case 'date':
                setDateFilter(value);
                break;
            case 'author':
                setAuthorFilter(value);
                break;
            case 'streck':
                setStreckFilter(value);
                break;
            default:
                break;
        }
    };

    const createDateStr = (comments: Comment[]) => {
        return comments.map((comment) => ({
            ...comment,
            dateStr: new Date(comment.date).toLocaleDateString('sv-SE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            }),
        }));
    }

    const searchComments = useCallback((comments: Comment[], searchTerm: string) => {
        if (!searchTerm) return comments;

        const options = {
            includeScore: true,
            keys,
        };

        const fuse = new Fuse(comments, options);
        const searchResults = fuse.search(searchTerm);

        return searchResults.filter(({ score }) => score < SCORE_THRESHOLD).map(({ item }) => item);
    }, []);

    const filterComments = useCallback((comments: Comment[]) => {

        let filtered = comments.filter((comment) => {
            if (dateFilter) {
                const commentDate = new Date(comment.date);

                switch (dateFilter) {
                    case 'spring':
                        if (commentDate.getMonth() < 0 || commentDate.getMonth() > 5) {
                            return false;
                        }
                        break;
                    case 'fall':
                        if (commentDate.getMonth() < 6 || commentDate.getMonth() > 11) {
                            return false;
                        }
                        break;
                    default:
                        break;
                }
            }
            if (authorFilter && comment.position !== authorFilter) {
                return false;
            }
            if (streckFilter && comment.streck !== streckFilter) {
                return false;
            }
            return true;
        }


        );
        if (dateFilter) {
            switch (dateFilter) {
                case 'latest-first':
                    filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                    break;
                case 'oldest-first':
                    filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                    break;
                default:
                    break;
            }
        }

        return filtered

    }, [dateFilter, authorFilter, streckFilter]);

    const scrollToBottom = () => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    useEffect(() => {
        // Sort comments by newest date first
        comments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        // Remove _id, updatedAt and __v from comments for CSV export
        const csvComments = comments.map((comment) => {
            const { _id,updatedAt,__v,createdAt, ...rest } = comment;            
            const parsedDate = new Date(createdAt);
            const dateField = parsedDate.toLocaleDateString('sv-SE');
            const timeField = parsedDate.toLocaleTimeString('sv-SE');

            return {...rest, date: dateField, time: timeField};
            
        });
    
    
        setCsvComments(csvComments);
        
        
        // Add dates as strings 
        const dateStrComments = createDateStr(comments);
        setDateStrComments(dateStrComments);
    }, [comments]);

    useEffect(() => {
        if (dateFilter !== null || authorFilter !== null || streckFilter !== null) {
            scrollToBottom();
        }
    }
        , [dateFilter, authorFilter, streckFilter, filteredComments]);

    useEffect(() => {
        let filtered = filterComments(dateStrComments);
        filtered = searchComments(filtered, searchTerm);
        setFilteredComments(filtered);
    }, [dateStrComments, searchTerm, filterComments, searchComments]);


    return (
        <div className='comment-table-container'>
            <div>

                <h2>Kommentarer</h2>
                {csvComments.length > 0 && (
                <CSVLink 
                className="comments-download" 
                data={csvComments} 
                headers={headers} 
                filename='strecklistan-kommentarer.csv'
                title='Ladda ner alla kommentarer'>
                    <FaFileDownload size={20} className="comments-download"/>
                </CSVLink>)}

            </div>

            {comments.length > 0 ?
                (
                    <>
                        <div className='comment-table-search-filter-container'>
                            <input type="text" value={searchTerm} onChange={handleSearchChange} placeholder="Sök efter kommentarer." />
                            <Filters comments={comments} onFilterChange={handleFilterChange} />
                        </div>
                        {
                            filteredComments.length > 0 ? (
                                <Table striped bordered hover variant={theme}>
                                    <thead>
                                        <tr>
                                            <th>Post</th>
                                            <th>Kommentar</th>
                                            <th>Streck</th>
                                            <th>Antal</th>
                                            <th>Datum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredComments.map((comment, index) => (
                                            <motion.tr
                                                key={index}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.5 }}
                                            >
                                                <td>{comment.position}</td>
                                                <td>{comment.comment}</td>
                                                <td>{comment.streck}</td>
                                                <td>{comment.count}</td>
                                                <td>{comment.dateStr}</td>
                                            </motion.tr>
                                        ))}

                                    </tbody>

                                </Table>
                            ) : (
                                <p className='vertical-center'>Inga kommentarer. Testa ändra filtret.</p>

                            )
                        }
                    </>

                ) :
                <p className='vertical-center'>
                    Ingen har fått några kommentarer än. Testa igen senare.
                </p>
            }
            <div ref={commentsEndRef} />
        </div>
    );
}


export default CommentsTable;
